import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Loader2, CheckCircle2, FileDown, UserCheck, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { EmailHint } from '../components/EmailHealth';

const API = process.env.REACT_APP_BACKEND_URL;
const inp = 'w-full px-3 py-2 rounded border text-sm bg-white';
const bd = { borderColor: 'rgba(0,0,0,0.2)' };
const EMPTY = {
  surname: '', first_name: '', next_of_kin: '', nok_relationship: '', nok_phone: '', address: '', postcode: '', phone_home: '', phone_work: '', mobile: '', email: '',
  service_corps: '', service_number: '', rank: '', enlistment_date: '', discharge_date: '', trade: '', decorations: '',
  postings: [{ unit: '', from_date: '', to_date: '' }], overseas_service: false, overseas_country: '', occupation: '',
  signed_name: '', signed_date: new Date().toISOString().slice(0, 10), nominated_by: '', nominated_date: '', prefill_code: '',
};
const DRAFT_KEY = 'membership-apply-draft';
const APP_KEYS = Object.keys(EMPTY);
const pickForm = (src) => Object.fromEntries(APP_KEYS.map((k) => [k, src && src[k] !== undefined && src[k] !== null ? src[k] : EMPTY[k]]));

function Field({ label, k, form, set, type = 'text', required, span, placeholder }) {
  return (
    <label className={`block text-sm ${span ? 'sm:col-span-2' : ''}`}>
      <span style={{ color: 'var(--navy)' }}>{label}{required && <span style={{ color: '#8a1f11' }}> *</span>}</span>
      <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} required={required} placeholder={placeholder} data-testid={`apply-${k}`} className={`${inp} mt-1`} style={bd} />
    </label>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-lg p-5 border shadow-sm" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <h2 className="font-title text-lg mb-3 pb-2 border-b" style={{ color: 'var(--maroon)', borderColor: 'var(--gold)' }}>{title}</h2>
      <div className="grid sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

export default function MembershipApply() {
  const { user, loaded, getApplicationInfo, submitApplication, getApplicationDraft, saveApplicationDraft, deleteApplicationDraft, getApplicationPrefill } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [restored, setRestored] = useState(null); // 'draft' | 'submitted' | 'link' | null
  const [prefillName, setPrefillName] = useState('');
  const [draftState, setDraftState] = useState(''); // '' | 'saving' | 'saved'
  const hydrated = useRef(false);
  const dirty = useRef(false);

  useEffect(() => { getApplicationInfo().then(setInfo).catch(() => {}); /* eslint-disable-next-line */ }, []);

  // Restore: server draft → last submitted application → browser draft → profile defaults.
  useEffect(() => {
    if (!loaded || hydrated.current) return;
    hydrated.current = true;
    (async () => {
      const apply = (f, why) => { if (!dirty.current) { setForm(f); setRestored(why); } };
      const code = new URLSearchParams(window.location.search).get('t');
      if (code) {
        try {
          const p = await getApplicationPrefill(code);
          apply({ ...EMPTY, ...pickForm({ ...EMPTY, ...p.form }), prefill_code: p.form.prefill_code }, 'link');
          setPrefillName(p.name);
          return;
        } catch { setErr('This personal link has expired — please fill in the form below.'); }
      }
      let local = null;
      try { local = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch { local = null; }
      if (user) {
        try {
          const d = await getApplicationDraft();
          if (d.draft?.form) { apply(pickForm(d.draft.form), 'draft'); return; }
          if (d.last_submitted) { apply(pickForm(d.last_submitted), 'submitted'); return; }
        } catch { /* fall through */ }
      }
      if (local) { apply(pickForm(local), 'draft'); return; }
      if (user && !dirty.current) {
        const [fn, ...rest] = (user.name || '').split(' ');
        setForm((f) => ({ ...f, email: user.email || '', first_name: user.first_name || fn || '', surname: user.last_name || rest.join(' ') || '',
          address: user.address || '', postcode: user.postcode || '', phone_home: user.phone || '', mobile: user.mobile || '', rank: user.rank || '',
          next_of_kin: user.next_of_kin || '', nok_relationship: user.nok_relationship || '', nok_phone: user.nok_phone || '' }));
      }
    })();
    /* eslint-disable-next-line */
  }, [loaded, user]);

  // Autosave (debounced): server for signed-in members, browser otherwise.
  useEffect(() => {
    if (!hydrated.current || !dirty.current || done) return;
    setDraftState('saving');
    const t = setTimeout(async () => {
      try {
        if (user) await saveApplicationDraft(form); else localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
        setDraftState('saved');
      } catch { setDraftState(''); }
    }, 1200);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [form]);

  const set = (k, v) => { dirty.current = true; setForm((f) => ({ ...f, [k]: v })); };
  const setPosting = (i, k, v) => { dirty.current = true; setForm((f) => ({ ...f, postings: f.postings.map((p, j) => (j === i ? { ...p, [k]: v } : p)) })); };
  const addPosting = () => { dirty.current = true; setForm((f) => ({ ...f, postings: [...f.postings, { unit: '', from_date: '', to_date: '' }] })); };
  const removePosting = (i) => { dirty.current = true; setForm((f) => ({ ...f, postings: f.postings.filter((_, j) => j !== i) })); };
  const startAgain = async () => {
    if (!window.confirm('Clear everything you have entered and start a blank form?')) return;
    dirty.current = false; setForm(EMPTY); setRestored(null); setDraftState('');
    localStorage.removeItem(DRAFT_KEY);
    if (user) { try { await deleteApplicationDraft(); } catch { /* ignore */ } }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!form.mobile && !form.phone_home && !form.phone_work) { setErr('Please provide at least one phone number so the Secretary can contact you.'); return; }
    if (!form.signed_name.trim()) { setErr('Please type your full name as your signature.'); return; }
    setBusy(true);
    try { const r = await submitApplication(form); localStorage.removeItem(DRAFT_KEY); setDone(r.id); window.scrollTo(0, 0); }
    catch (ex) { setErr((ex.response && ex.response.data && ex.response.data.detail) || 'Could not submit the application. Please check the form and try again.'); }
    finally { setBusy(false); }
  };

  const org = (info && info.org) || {};
  const fee = info ? `$${((info.fee_cents || 0) / 100).toFixed(2)}` : '';

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center fade-in" data-testid="apply-success">
        <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: 'var(--maroon)' }} />
        <h1 className="font-title text-3xl mb-2" style={{ color: 'var(--maroon)' }}>Application received</h1>
        <p className="mb-2">Thank you, {form.first_name}. The committee reviews applications at its next meeting and the Secretary will email you at <b>{form.email}</b>.</p>
        <p className="text-sm" style={{ color: 'var(--olive)' }}>Please don't pay the {fee} subscription yet — payment details are sent once your application is accepted. Reference: {done}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 fade-in" data-testid="apply-page">
      <h1 className="font-title text-3xl md:text-4xl mb-2" style={{ color: 'var(--maroon)' }}>Application for Membership</h1>
      <div className="h-1 w-20 mb-4" style={{ background: 'var(--gold)' }} />
      <p className="text-[15px] mb-2">Complete the form below. The committee reviews each application before membership is confirmed; the annual subscription{fee ? ` (${fee})` : ''} is requested only after acceptance.</p>
      <p className="text-sm mb-6" style={{ color: 'var(--olive)' }}>
        Prefer paper? <a href={`${API}/api/membership/application-form.pdf`} className="underline inline-flex items-center gap-1" style={{ color: 'var(--navy)' }} data-testid="apply-blank-pdf"><FileDown size={14} /> Download the printable form</a>
        {org.postal_address ? ` and post it to the Secretary, ${org.secretary_name ? org.secretary_name + ', ' : ''}${org.postal_address}.` : '.'}
      </p>
      {user ? (
        <p className="text-sm mb-4 inline-flex items-center gap-2 px-3 py-2 rounded" style={{ background: 'var(--sage)' }} data-testid="apply-logged-in"><UserCheck size={15} /> Signed in as {user.email} — your contact details have been filled in; please check them.</p>
      ) : (
        <p className="text-sm mb-4 px-3 py-2 rounded" style={{ background: 'var(--sage)' }} data-testid="apply-guest">You're not signed in. Please enter your contact details carefully — they create your contact record so the Secretary can reach you. Already have an account? <a href="/login" className="underline" style={{ color: 'var(--navy)' }}>Log in</a> first to pre-fill them.</p>
      )}

      {restored && (
        <div className="text-sm mb-4 px-3 py-2 rounded flex flex-wrap items-center gap-2" style={{ background: '#fff8e6', border: '1px solid #e6cf8a' }} data-testid="apply-restored">
          <History size={15} />
          <span>{restored === 'draft' ? 'We restored what you entered earlier — carry on where you left off.'
            : restored === 'link' ? `Pre-filled from our records for ${prefillName || 'you'} — please check every detail, complete the rest and sign.`
              : 'Pre-filled from your last submitted application — check, edit and resubmit if anything has changed.'}</span>
          <button type="button" onClick={startAgain} className="underline ml-auto" style={{ color: 'var(--navy)' }} data-testid="apply-start-again">Start again</button>
        </div>
      )}
      {draftState && <p className="text-xs mb-2" style={{ color: 'var(--olive)' }} data-testid="apply-draft-state">{draftState === 'saving' ? 'Saving draft…' : `Draft saved${user ? ' to your account' : ' in this browser'}.`}</p>}

      <form onSubmit={submit} className="space-y-5" data-testid="apply-form">
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website || ''} onChange={(e) => set('website', e.target.value)} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} data-testid="apply-honeypot" />
        <Section title="Personal details">
          <Field label="Surname" k="surname" form={form} set={set} required />
          <Field label="First name" k="first_name" form={form} set={set} required />
          <Field label="Address" k="address" form={form} set={set} required />
          <Field label="Postcode" k="postcode" form={form} set={set} />
          <Field label="Phone (home)" k="phone_home" form={form} set={set} type="tel" />
          <Field label="Phone (work)" k="phone_work" form={form} set={set} type="tel" />
          <Field label="Mobile" k="mobile" form={form} set={set} type="tel" />
          <div><Field label="Email" k="email" form={form} set={set} type="email" required /><EmailHint value={form.email} testid="apply-email-hint" /></div>
        </Section>

        <Section title="Next of kin">
          <Field label="Name of next of kin" k="next_of_kin" form={form} set={set} />
          <Field label="Relationship" k="nok_relationship" form={form} set={set} placeholder="e.g. Spouse, Daughter" />
          <Field label="Next of kin phone" k="nok_phone" form={form} set={set} type="tel" />
        </Section>

        <Section title="Service details">
          <Field label="Service / Corps" k="service_corps" form={form} set={set} placeholder="e.g. Army / RAE" />
          <Field label="Service number" k="service_number" form={form} set={set} />
          <Field label="Rank" k="rank" form={form} set={set} />
          <Field label="Trade" k="trade" form={form} set={set} />
          <Field label="Enlistment date" k="enlistment_date" form={form} set={set} type="date" />
          <Field label="Discharge date" k="discharge_date" form={form} set={set} type="date" />
          <Field label="Decorations & awards" k="decorations" form={form} set={set} span />
          <div className="sm:col-span-2">
            <div className="text-sm mb-1" style={{ color: 'var(--navy)' }}>Unit postings</div>
            <div className="space-y-2" data-testid="apply-postings">
              {form.postings.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_130px_130px_32px] gap-2 items-center">
                  <input value={p.unit} onChange={(e) => setPosting(i, 'unit', e.target.value)} placeholder="Unit" data-testid={`apply-posting-unit-${i}`} className={inp} style={bd} />
                  <input value={p.from_date} onChange={(e) => setPosting(i, 'from_date', e.target.value)} placeholder="From" data-testid={`apply-posting-from-${i}`} className={inp} style={bd} />
                  <input value={p.to_date} onChange={(e) => setPosting(i, 'to_date', e.target.value)} placeholder="To" data-testid={`apply-posting-to-${i}`} className={inp} style={bd} />
                  <button type="button" onClick={() => removePosting(i)} disabled={form.postings.length === 1} title="Remove" data-testid={`apply-posting-remove-${i}`} className="disabled:opacity-30" style={{ color: '#8a1f11' }}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addPosting} data-testid="apply-add-posting" className="mt-2 inline-flex items-center gap-1 text-sm underline" style={{ color: 'var(--navy)' }}><Plus size={14} /> Add another posting</button>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.overseas_service} onChange={(e) => set('overseas_service', e.target.checked)} data-testid="apply-overseas_service" /> Overseas service</label>
          <Field label="Country / countries" k="overseas_country" form={form} set={set} />
          <Field label="Current occupation" k="occupation" form={form} set={set} span />
        </Section>

        <Section title="Declaration">
          <Field label="Signed (type your full name)" k="signed_name" form={form} set={set} required />
          <Field label="Date" k="signed_date" form={form} set={set} type="date" />
          <Field label="Nominated by (existing member, if any)" k="nominated_by" form={form} set={set} />
          <Field label="Nomination date" k="nominated_date" form={form} set={set} type="date" />
        </Section>

        <div className="rounded-lg p-4 text-sm" style={{ background: 'var(--sage)' }} data-testid="apply-subscription-note">
          <b>Subscription — {fee || '…'} per annum.</b> The committee reviews applications first; once accepted you'll receive an email with a link to pay online or by bank transfer{org.bank_name ? ` (${org.bank_name}${org.bank_bsb ? `, BSB ${org.bank_bsb}` : ''}${org.bank_account_number ? `, Acct ${org.bank_account_number}` : ''}${org.payid ? `, PayID ${org.payid}` : ''})` : ''}.
        </div>

        {err && <p className="text-sm px-3 py-2 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid="apply-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="apply-submit" className="inline-flex items-center gap-2 px-6 py-3 rounded font-title text-base" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
          {busy && <Loader2 size={16} className="animate-spin" />} Submit application
        </button>
      </form>
    </div>
  );
}
